import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { COVER_EXPORT_PX } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import type { CoverLeadError } from './coverLead';
import { coverLeadErrorMessages, publicCoverMessages } from './publicCoverMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from './PublicCoverMaker.module.scss';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';

interface EmailDialogProps {
  isOpen: boolean;
  isBusy: boolean;
  error: Exclude<CoverLeadError, 'down'> | null;
  onSubmit: (email: string) => void;
  onChange: () => void;
  onCancel: () => void;
}

/**
 * The email dialog of the public tool (owner decision D12; design cover-editor g3): the first
 * download of a browser asks for an email. It says what we do with the address and which file
 * comes. The button is off until the field has text; the API checks the address.
 */
const EmailDialogComponent: React.FC<EmailDialogProps> = ({ isOpen, isBusy, error, onSubmit, onChange, onCancel }) => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const change = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      onChange();
    },
    [onChange]
  );
  const submit = useCallback(() => onSubmit(email), [email, onSubmit]);
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && email.trim() !== '' && !isBusy) {
        onSubmit(email);
      }
    },
    [email, isBusy, onSubmit]
  );
  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(publicCoverMessages.emailTitle)}
      description={intl.formatMessage(publicCoverMessages.emailText)}
      confirmLabel={intl.formatMessage(isBusy ? publicCoverMessages.emailSaving : publicCoverMessages.emailConfirm)}
      cancelLabel={intl.formatMessage(publicCoverMessages.emailCancel)}
      isBusy={isBusy}
      isConfirmDisabled={email.trim() === ''}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <input
        className={error === null ? styles.emailInput : `${styles.emailInput} ${styles.emailInputBad}`}
        type="email"
        inputMode="email"
        autoComplete="email"
        // The dialog opens for this one field.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={email}
        placeholder={intl.formatMessage(publicCoverMessages.emailPlaceholder)}
        aria-label={intl.formatMessage(publicCoverMessages.emailLabel)}
        aria-invalid={error !== null}
        disabled={isBusy}
        onChange={change}
        onKeyDown={onKeyDown}
      />
      {error !== null && (
        <p className={styles.emailError} role="alert">
          {intl.formatMessage(coverLeadErrorMessages[error])}
        </p>
      )}
      <p className={styles.dialogHint}>{withBrandWord(intl.formatMessage(publicCoverMessages.emailWhat))}</p>
      <p className={styles.dialogFile}>
        {withBrandWord(intl.formatMessage(publicCoverMessages.emailFile, { size: COVER_EXPORT_PX }))}
      </p>
    </ConfirmDialog>
  );
};

export const EmailDialog = TypedMemo(EmailDialogComponent);
